import { apiControllerHelper } from 'api-controller-helpers/ApiControllerHelper';
import { ModelProperty } from 'models/Model';
declare const JSON: { decycle: Function, retrocycle: Function, stringify: (data: any) => string, parse: (value: string) => any };

export class RelationService {

	static deepClone<T extends Object | []>(object: T): T {
		return this.parse(this.stringify(object));
	}
	static stringify<T extends Object | []>(object: T): string {
		return JSON.stringify(this.decycle(object));
	}
	static parse<T extends Object | []>(value: string): T {
		return this.retrocycle(JSON.parse(value));
	}
	static decycle<T extends Object | []>(object: T): T {
		return JSON.decycle(object);
	}
	static retrocycle<T extends Object | []>(object: T): T {
		return JSON.retrocycle(object);
	}

	static removeRelationProperties(objects: any[]): any {
		objects = this.removeRelationPropertiesRecursive(objects, [], new Map<Object, Object>());
		return this.decycle(objects);
	}

	static removeRelationPropertiesRecursive(objects: any[], includedRelations: string[], traversedObjects: Map<Object, Object>): any[] {
		for (let index = 0; index < objects.length; index++) {
			const object = objects[index];
			if (!(object instanceof Object)) continue;

			if (object instanceof Array) {
				objects[index] = this.removeRelationPropertiesRecursive(object, includedRelations, traversedObjects);
				continue;
			}
			if (traversedObjects.has(object)) {
				objects[index] = traversedObjects.get(object);
				continue;
			}
			
			if (!object.getIncludedRelationProperties) continue;

			includedRelations = includedRelations.concat(object.getIncludedRelationProperties());
			const relationProperties = object.getProperties().filter((p: ModelProperty) => p.isObject);

			const newObject = objects[index] = { ...object };
			traversedObjects.set(object, newObject);
			
			for (const property of relationProperties) {
				if (includedRelations.indexOf(property.name) == -1) {
					if (property.isArray) {
						newObject[property.name] = [];
					} else {
						newObject[property.name] = undefined;
					}
				} else {
					if (property.isArray) {
						newObject[property.name] = this.removeRelationPropertiesRecursive(newObject[property.name], includedRelations, traversedObjects);
					} else {
						newObject[property.name] = this.removeRelationPropertiesRecursive([newObject[property.name]], includedRelations, traversedObjects)[0];
					}
				}
			}
		}
		return [...objects];
	}

	static instantiateModelsForControllerMethod(controller: string, method: string, receivedData: any): any {
		controller = controller.toLocaleLowerCase() + 'controller';
		if (apiControllerHelper.has(controller)) {
			const foundApiControllerHelperHelper = apiControllerHelper.get(controller);
			if (foundApiControllerHelperHelper)
				receivedData = this.instantiateModelsFromHelper(foundApiControllerHelperHelper, method, receivedData);
		}
		return receivedData;
	}

	private static instantiateModelsFromHelper(helper: Map<string, Object>, method: string, receivedData: any): any {
		if (helper && helper.has(method)) {
			if (receivedData instanceof Array) {
				for (let index in receivedData) {
					if (!receivedData.hasOwnProperty(index))
						continue;
					const newClass = Object.setPrototypeOf({}, Object.create(helper.get(method) as Object).prototype);
					receivedData[index] = this.instantiateModelsFromData(newClass, receivedData[index]);
				}
			} else {
				const newClass = Object.setPrototypeOf({}, Object.create(helper.get(method) as Object).prototype);
				receivedData = this.instantiateModelsFromData(newClass, receivedData);
			}
		}
		return receivedData;
	}

	static instantiateModelsFromData(containerObj: any, receivedData: any): any {
		if (!receivedData) return null;
		if (containerObj.getProperties) {
			const dateProperties = containerObj.getProperties().filter((p: ModelProperty) => p.type === 'Date');
			for (const property of dateProperties) {
				if (!receivedData[property.name]) continue;
				receivedData[property.name] = new Date(receivedData[property.name] + (receivedData[property.name].substr(19) != '' ? '' : 'Z'));
			}


			const relationProperties = containerObj.getProperties().filter((p: ModelProperty) => p.isObject);
			for (const property of relationProperties) {
				const receivedPropertyData = receivedData[property.name];
				if (receivedPropertyData == undefined)
					continue;
				if (property.isArray) {
					if (receivedPropertyData.length === 0)
						continue;
					for (let index in receivedPropertyData) {
						if (!receivedPropertyData.hasOwnProperty(index))
							continue;
						const dataClass = property.new();
						if (!receivedData[property.name][index].hasOwnProperty('$ref'))
							receivedData[property.name][index] = this.instantiateModelsFromData(dataClass, receivedPropertyData[index]);
					}
				} else {
					const dataClass = property.new();
					if (!receivedData[property.name].hasOwnProperty('$ref'))
						receivedData[property.name] = this.instantiateModelsFromData(dataClass, receivedPropertyData);
				}
			}
		}
		return Object.assign(containerObj, receivedData);
	}

	static flattenObject(obj: any): any {
		let result: any = {};
		function recurse(cur: any, prop: string) {
			if (Object(cur) !== cur) {
				result[prop] = cur;
			} else if (Array.isArray(cur)) {
				for (var i = 0, l = cur.length; i < l; i++)
					recurse(cur[i], prop + "[" + i + "]");
				if (l == 0)
					result[prop] = [];
			} else {
				var isEmpty = true;
				for (var p in cur) {
					isEmpty = false;
					recurse(cur[p], prop ? prop + "." + p : p);
				}
				if (isEmpty && prop)
					result[prop] = {};
			}
		}
		recurse(obj, "");
		return result;
	}

	static flattenArrayOfObjects<T extends StrIndex<any>>(arr: T[], recursiveProperty: string): T[] {
		return arr.reduce((result: T[], currentObj: T) => {
			result.push(currentObj);
			if (currentObj[recursiveProperty] && currentObj[recursiveProperty].length) {
				result = result.concat(this.flattenArrayOfObjects(currentObj[recursiveProperty], recursiveProperty));
			}
			return result;
		}, [])
	}
	
}

type StrIndex<TValue> = {
	[key: string]: TValue
}
